/**
 * Bubbles, what they hold and the items they leave (spec 02 §4, §7.1, §7.2, §7.6, §7.7, §8) - owned by
 * the BUBBLE stream, ported from the rebb64 disassembly of the C64 game. The world calls `updateBubbles`
 * every M-tick (one main-loop pass) and `updateItems` every I-tick; the PLAYER stream calls
 * `blowBubble` on the blow's 3rd tick; the flow calls `levelClearBonus`; the other streams call
 * `killMonsterBy`. README.md: the coordinates, and what the renderer reads.
 */

import { hasLightning, takeRedCross } from '../items';
import { blowOrigin, pushBubbles } from '../players';
import { boltView, boltsHitBoss, fireRedCross } from './bolts';
import { clearBonus, giantTick } from './bonus';
import { POPPING, isBubbleCode } from './codes';
import { checkExtend } from './extend';
import { burnFires, fireView } from './fire';
import { moveBubbles } from './float';
import { killBy, updateDeathItems } from './items';
import { burstPass } from './pop';
import { shootBubble } from './shot';
import { freeSlot, setCode } from './slots';
import { spawnFromHoles } from './spawner';
import { clearSweep, runTimers } from './timers';
import { LEVEL_100, POTION_TIMER } from './tuning';
import { waterTick } from './water';
import type { Facing, PlayerId, World } from '../types';

export { bounceable, bubbleCell, bubbleLook, pushable, waterCells, type BubbleLook } from './view';
export { deathFlight, type DeathFlight } from './items';
export { DEATH_PRIZES, FOODS, deathPrize, foodName, type Food, type Prize } from './food';
export { carriedByWater } from './water';
export { placeBubble, type PlacedBubble } from './scene';
export { shootBubble } from './shot';
export { EXTEND_FOOD_FRAMES, GIANT_BONUS, scheduledFood } from './tuning';

/**
 * The PLAYER stream calls this on the blow animation's 3rd tick (02 §3 Blow; D_2301 L232A): a shot at
 * the player's `blowOrigin` (X, +8 facing right; +8 lower mid-jump), 12 steps with purple candy and
 * two a pass with blue (the player's $A77D / $A77F / $A781). With no slot free, or above Y $15, nothing
 * comes out (false). Holding the red cross (L23B8: `takeRedCross`, the ITEMS stream's) a bolt flies
 * instead of a bubble; on level 100 a lightning-potion holder's bubble is marked to turn to lightning
 * at the end of its shot (D_23DE: its timer 8 + player).
 */
export function blowBubble(world: World, id: PlayerId): boolean {
  const p = world.players[id];
  const o = blowOrigin(p);
  if (freeSlot(world) < 0 || o.y - 0x15 < 0) return false;
  if (takeRedCross(world, id)) {
    fireRedCross(world, id, o.x, o.y, o.facing);
    return true;
  }
  const candy = { purple: p.motion.purpleCandy, blue: p.motion.blueCandy };
  const b = shootBubble(world, id, o.x, o.y, o.facing, candy);
  if (b && world.levelIndex === LEVEL_100 && hasLightning(world, id)) b.motion.timer = POTION_TIMER | id;
  return b !== null;
}

/**
 * One M-tick, in the order of the C64's main loop ($0A07): the level-clear sweep (in D_1578), the
 * burning lines (D_1319), the hole spawner (D_0BED; on level 100 the boss's bolt check $1134 in its
 * place), the drawing pass that bursts and chains (D_E90E), the players' pushes (D_0AAB, the PLAYER
 * stream's `pushBubbles`), the entity pass - shots and trapping, touches, floating, bunching, wrapping,
 * drops, bolts, bubble food (D_0CF2) - then lifetimes, blinking, escapes and crowding (D_13BE), and
 * EXTEND (D_32C1).
 */
export function updateBubbles(world: World): void {
  clearSweep(world);
  burnFires(world);
  spawnFromHoles(world);
  boltsHitBoss(world);
  burstPass(world);
  pushBubbles(world);
  moveBubbles(world);
  runTimers(world);
  checkExtend(world);
  for (const b of world.bubbles) b.motion.age++;
}

/**
 * One I-tick: the dead monsters' flight, fall and items (sprite states $0B..$12), the giant bonus
 * (state 21), and the water stream (D_1B40) with what it carries. (On the C64 the stream runs first;
 * the items go first here so that a monster the water drops this tick starts falling on the next, as
 * its state $0C does.)
 */
export function updateItems(world: World): void {
  updateDeathItems(world);
  giantTick(world);
  waterTick(world);
}

/**
 * Kill a monster outside a bubble (M2 contract, for the other streams): `how` picks its death item
 * (02 §7.2: fire a green diamond, lightning or any other strike - bolt, bomb, lamp, cross - a purple
 * diamond, heart the grapes), and the `monsterDied` event says 'fire', 'lightning' or 'other' (the
 * heart). It flies off and lands as that item, as a burst monster does (D_1A6F). One held in a bubble
 * is let out first, the bubble vanishing. Nothing happens to a monster already dead. `by` is who set
 * it off: the C64 pays nobody at such a kill (no chain, no points; the item is anyone's), so it only
 * documents the call.
 */
export function killMonsterBy(world: World, monsterId: number, how: 'fire' | 'lightning' | 'heart' | 'other', by: PlayerId | -1): void {
  void by;
  killBy(world, monsterId, how);
}

/**
 * The level is clear (02 §7.6; the flow calls this once, as the last monster dies): with `bubbleFood`
 * or a staff or box (`giant`, its POWERUPS index: 1, 2, 21, 23, 25, 26, 27) every plain bubble on
 * screen becomes a falling 700-point `bubbleFood` item (drawn as the scheduled food, or the staff's or
 * box's: cupcake, melon, yellow / green / purple diamond); with a staff or box every death item goes
 * and the `giant` item drops from the top (4 px an I-tick, landing after 2 s), worth 10,000-50,000.
 */
export function levelClearBonus(world: World, bonus: { bubbleFood: boolean; giant: number }): void {
  clearBonus(world, bonus);
}

/** What the renderer draws of fire (02 §8): falling drops, and the burning cells. VIC frame / tiles. */
export interface FireLook {
  /** Each drop's 16x16 top-left, as a bubble's. */
  drops: { x: number; y: number }[];
  /**
   * Burning cells: tile column and row (row 0 the top); `deadly` while it still kills; `frame` the C64's
   * flame (0 low - lit and dying down; 1, 2 the tall ones, alternating).
   */
  cells: { col: number; row: number; deadly: boolean; frame: 0 | 1 | 2 }[];
}

/** The fire after the last pass. */
export function fireLook(world: World): FireLook {
  return fireView(world);
}

/** A lightning or red-cross bolt in flight (02 §8, §7.5): its 16x16 top-left as a bubble's, and its way. */
export interface BoltLook {
  x: number;
  y: number;
  dir: Facing;
  /** Who set it off (-1 nobody): a red-cross bolt is its player's colour. */
  player: PlayerId | -1;
  redCross: boolean;
}

/** The bolts after the last pass, where the C64 draws them (their column and row). */
export function boltLooks(world: World): BoltLook[] {
  return boltView(world);
}

/**
 * Burst a bubble, credited to `by` (a player, or -1: no points, no chain count): it becomes a bursting
 * bubble ($34, as a touch makes it), and the next M-tick's pass releases what it holds and spreads the
 * chain. Nothing happens to a bubble already bursting or gone.
 */
export function popBubble(world: World, bubbleId: number, by: PlayerId | -1): void {
  const b = world.bubbles.find((x) => x.id === bubbleId);
  if (!b || !isBubbleCode(b.motion.code)) return;
  b.motion.saved = b.motion.code;
  b.motion.credit = by;
  setCode(b, POPPING);
}
